import {
  PLATFORM,
  getDomain,
  isDomainId,
  listDomains,
  type DomainDefinition,
  type DomainId,
} from '../lib/DomainRegistry.js';
import { LEVEL_LABEL, loadDetailIndex, type Applicability } from '../lib/SystemsHierarchy.js';
import { projectStore, type Cue, type CueItem } from '../lib/ProjectContainer.js';

/** Panels with their own listeners mount on a child of the shared host, so a replaced view cannot keep acting on it. */
function mountPoint(container: HTMLElement): HTMLElement {
  const el = document.createElement('div');
  el.className = 'ws-mount';
  container.appendChild(el);
  return el;
}

/**
 * Public (non-admin) services. `overview` is the Systems Lab view; each
 * enabled domain is a service of its own. The old 'digital-twin-workflow'
 * id resolves to the building domain.
 */
export type PublicServiceId = 'overview' | 'start' | 'setup' | 'models' | 'narrative' | DomainId;

type AllServices = PublicServiceId | 'admin';

export const DEFAULT_SERVICE: PublicServiceId = 'overview';

const LEGACY_SERVICE_ALIASES: Record<string, PublicServiceId> = {
  'digital-twin-workflow': 'building',
};

export function resolvePublicService(id: string | null | undefined): PublicServiceId | null {
  if (!id) return null;
  if (id === 'overview') return 'overview';
  if (id === 'start') return 'start';
  if (id === 'setup') return 'setup';
  if (id === 'models') return 'models';
  if (id === 'narrative') return 'narrative';
  const aliased = LEGACY_SERVICE_ALIASES[id] ?? id;
  if (isDomainId(aliased) && getDomain(aliased)?.enabled) return aliased;
  return null;
}

/** `#/`, `#/lab` → the Systems Lab; `#/building`, `#/landscape` → domains. */
export function serviceFromHash(hash: string): PublicServiceId | null {
  const path = hash.replace(/^#\/?/, '').replace(/\/+$/, '').trim();
  if (path === '' || path === 'lab' || path === 'overview') return 'overview';
  return resolvePublicService(path);
}

export function hashForService(id: PublicServiceId): string {
  return id === 'overview' ? '#/' : `#/${id}`;
}

export interface ServiceState {
  isActive: boolean;
  lastAccessed: Date;
  context?: any;
}

export interface DigitalTwinWorkflowContext {
  selectedElements: string[];
  currentComponent?: any;
  analysisPhase: "conceptual" | "detailed" | "as-built";
  properties: Map<string, any>;
}

/** Fired after every public render so the Workspace can update docks and status. */
export const CONTEXT_EVENT = 'ws-context';

/**
 * ServiceRouter renders the center view: the Systems Lab, one terse page
 * per domain, and the protected admin panel. Long copy is not rendered
 * here; it lives in the Info dock, which reads the same registry.
 */
export class ServiceRouter {
  private currentService: AllServices = DEFAULT_SERVICE;
  private serviceStates: Map<string, ServiceState> = new Map();
  private workflowContext: DigitalTwinWorkflowContext | null = null;
  private protectedAdminRoute: import("./ProtectedAdminRoute.js").ProtectedAdminRoute | null = null;
  private pendingAdminTab: string | null = null;

  constructor() {
    this.serviceStates.set('overview', { isActive: false, lastAccessed: new Date(), context: null });
    this.serviceStates.set('start', { isActive: false, lastAccessed: new Date(), context: null });
    this.serviceStates.set('setup', { isActive: false, lastAccessed: new Date(), context: null });
    this.serviceStates.set('models', { isActive: false, lastAccessed: new Date(), context: null });
    this.serviceStates.set('narrative', { isActive: false, lastAccessed: new Date(), context: null });
    for (const domain of listDomains()) {
      this.serviceStates.set(domain.id, { isActive: false, lastAccessed: new Date(), context: null });
    }
    this.serviceStates.set('admin', { isActive: false, lastAccessed: new Date(), context: null });
  }

  public switchService(serviceId: AllServices): void {
    if (serviceId === "admin") {
      console.warn('Use switchToProtectedService for the admin route.');
      return;
    }
    const resolved = resolvePublicService(serviceId) ?? DEFAULT_SERVICE;
    this.saveServiceState(this.currentService);
    this.currentService = resolved;
    this.serviceStates.forEach((state, id) => {
      state.isActive = id === resolved;
      if (id === resolved) state.lastAccessed = new Date();
    });
    this.syncHash(resolved);
    void this.renderServiceArea();
  }

  private syncHash(serviceId: PublicServiceId): void {
    if (typeof window === 'undefined') return;
    const current = window.location.hash || '#/';
    if (serviceFromHash(current) !== serviceId) {
      window.history.replaceState(null, '', hashForService(serviceId));
    }
  }

  private saveServiceState(serviceId: AllServices): void {
    const state = this.serviceStates.get(serviceId);
    if (state && serviceId !== 'admin') state.context = this.workflowContext;
  }

  private async renderServiceArea(): Promise<void> {
    const container = document.getElementById("service-container");
    if (!container) return;
    container.innerHTML = "";

    if (this.currentService === 'admin') {
      await this.renderAdminService(container);
      this.emitContext({ service: 'admin', domain: null, hint: 'Admin' });
      return;
    }
    if (this.currentService === 'start') {
      const { StartPanel } = await import('./StartPanel.js');
      new StartPanel(mountPoint(container));
      this.wireView(container);
      this.emitContext({ service: 'start', domain: null, hint: 'Start · a project, a scheme, or one you hold' });
      return;
    }
    if (this.currentService === 'setup') {
      const { ProjectSetupPanel } = await import('./ProjectSetupPanel.js');
      new ProjectSetupPanel(mountPoint(container));
      this.wireView(container);
      this.emitContext({ service: 'setup', domain: null, hint: 'Project setup · bind, author, permit map' });
      return;
    }
    if (this.currentService === 'models') {
      const { ModelsPanel } = await import('./ModelsPanel.js');
      new ModelsPanel(mountPoint(container));
      this.emitContext({ service: 'models', domain: null, hint: 'Models · upload, normalize, link to the project' });
      return;
    }
    if (this.currentService === 'narrative') {
      const { ZoneBriefsPanel } = await import('./ZoneBriefsPanel.js');
      new ZoneBriefsPanel(mountPoint(container));
      this.emitContext({ service: 'narrative', domain: null, hint: 'Zone briefs · what each zone is for, needs, runs, watches' });
      return;
    }
    const domain = this.currentService === 'overview' ? undefined : getDomain(this.currentService);
    await loadDetailIndex();   // cached after the first call; lets the hierarchy list its detail components
    if (domain) this.renderDomainPage(container, domain);
    else this.renderLab(container);
    this.initializeWorkflowContext();
    this.emitContext({ service: this.currentService, domain: domain?.id ?? null });
  }

  private emitContext(detail: { service: string; domain: string | null; hint?: string }): void {
    window.dispatchEvent(new CustomEvent(CONTEXT_EVENT, { detail }));
  }

  // ── Systems Lab (center: ask, or now) ─────────────────────────────
  //
  // The center view asks and confirms; it never lists specifications. With a
  // container open it shows the cue for the current phase (what is owed, one
  // click each); without one it asks where to start. Systems, families, IFC
  // classes, specs and details live in the Info dock and open from chips.

  private renderLab(container: HTMLElement): void {
    const domains = listDomains();
    const project = projectStore.project;
    const cue = projectStore.cue;
    container.innerHTML = `
      <div class="ws-view" data-service="overview">
        <div class="ws-view-head">
          <h1>${esc(project ? project.name : PLATFORM.name)}</h1>
          <span class="ws-sub">${esc(project && cue ? cue.summary : PLATFORM.headline)}</span>
          <div class="ws-head-actions"><button class="ws-link-btn" data-info="${project ? 'cue' : 'about'}"><i class="fa-solid fa-circle-info"></i> Details</button></div>
        </div>

        ${project ? this.renderNowCard(cue, null) : this.renderAskCard()}

        <div class="ws-section">
          <div class="ws-section-title">Branches</div>
          <div class="ws-chips">
            ${domains.map(d => `<button class="ws-chip" data-nav="${d.id}" type="button" title="${esc(d.tagline)}"><i class="fa-solid ${d.icon}" style="color:${d.accent}"></i> ${esc(d.navTitle)}</button>`).join('')}
            <button class="ws-chip" data-info="pattern" type="button" title="${esc(PLATFORM.pattern.map(s => s.title).join(' · '))}"><i class="fa-solid fa-route"></i> The pattern</button>
          </div>
        </div>

        <div class="ws-trust">${esc(PLATFORM.trustLine)}</div>
      </div>`;
    this.wireView(container);
  }

  /** No container: one question, the ways in. The Start view carries the interview itself. */
  private renderAskCard(): string {
    return `
      <section class="ws-card ws-ask">
        <div class="ws-start-ask">Where would you like to start?</div>
        <div class="ws-chips">
          <button class="ws-chip" data-nav="start" type="button"><i class="fa-solid fa-circle-play"></i> Start a project</button>
          <button class="ws-chip" data-nav="start" type="button"><i class="fa-solid fa-file-pdf"></i> Upload a scheme</button>
          <button class="ws-chip" data-nav="start" type="button"><i class="fa-solid fa-folder-open"></i> Open a project</button>
        </div>
        <p class="dim ws-note">A few questions, one at a time. You confirm what is recorded; the lab decides what the project calls for and keeps the detail in the Info dock.</p>
      </section>`;
  }

  /**
   * A container is open: the cue for the current phase. Each item is a title, a line,
   * and one way to act on it (a view, or the Info dock where the action lives). With a
   * domain, the systems this phase decides for that branch follow as pills.
   */
  private renderNowCard(cue: Cue | null, domainId: string | null): string {
    if (!cue) {
      return `<section class="ws-card ws-now"><div class="ws-start-ask">Now</div><p class="dim">${esc(projectStore.loading ? 'Reading the project…' : (projectStore.error ?? 'No cue yet.'))}</p></section>`;
    }
    const groups = domainId ? cue.systems_now.filter(g => g.domain === domainId) : [];
    const row = (item: CueItem, dim = false) => `
      <li class="ws-cue-item ${dim ? 'dim' : ''}">
        <div class="ws-cue-title"><b>${esc(item.title)}</b></div>
        ${item.detail ? `<div class="ws-cue-detail">${esc(item.detail)}</div>` : ''}
        <div class="ws-cue-how">${item.service
          ? `<button class="ws-link-btn" type="button" data-nav="${esc(item.service)}">Open</button>`
          : `<button class="ws-link-btn" type="button" data-info="${esc(item.section ?? 'cue')}">${dim ? 'Read' : 'Act'} in the Info dock</button>`}</div>
      </li>`;
    return `
      <section class="ws-card ws-now">
        <div class="ws-start-ask">Now · ${esc(cue.phase_label)}${cue.lod_current ? ` <span class="ws-pill" data-status="in-test">LOD ${cue.lod_current}</span>` : ''}</div>
        ${cue.now.length ? `<ul class="ws-cue-list">${cue.now.map(i => row(i)).join('')}</ul>` : '<p class="dim">Nothing owed in this phase.</p>'}
        ${cue.blocked.length ? `<p class="dim ws-note">Not yet: ${cue.blocked.map(b => `<span title="${esc(b.reason)}">${esc(b.title)}</span>`).join(' · ')}</p>` : ''}
        ${cue.next.length ? `<details class="ws-details"><summary>Next</summary><ul class="ws-cue-list">${cue.next.map(i => row(i, true)).join('')}</ul></details>` : ''}
        ${groups.length ? `<p class="ws-note">This phase decides: ${groups.map(g => `<span class="ws-pill" data-level="${esc(g.level ?? 'optional')}" title="${esc(g.reason ?? '')}">${esc(g.title)} · ${esc(LEVEL_LABEL[(g.level ?? 'optional') as Applicability])}</span>`).join(' ')} <button class="ws-link-btn" type="button" data-info="systems" style="padding:1px 7px">Why</button></p>` : ''}
      </section>`;
  }

  // ── Domain page (center: ask, or now, for one branch) ─────────────

  private renderDomainPage(container: HTMLElement, d: DomainDefinition): void {
    const project = projectStore.project;
    const cue = projectStore.cue;
    const what = d.navTitle.toLowerCase();
    container.innerHTML = `
      <div class="ws-view" data-service="${d.id}">
        <div class="ws-view-head">
          <h1><span class="ws-swatch" style="--swatch:${d.accent};display:inline-block;width:8px;height:8px;border-radius:2px;background:var(--swatch);margin-right:8px;vertical-align:middle"></span>${esc(d.title)}</h1>
          <span class="ws-sub">${esc(d.tagline)}</span>
          <div class="ws-head-actions">
            <button class="ws-link-btn" data-info="about"><i class="fa-solid fa-circle-info"></i> Details</button>
            <button class="ws-link-btn" data-nav="overview"><i class="fa-solid fa-flask"></i> Lab</button>
          </div>
        </div>

        ${project ? this.renderNowCard(cue, d.id) : `
        <section class="ws-card ws-ask">
          <div class="ws-start-ask">What would you like to do with ${esc(what)}?</div>
          <div class="ws-chips">
            <button class="ws-chip" data-nav="start" type="button"><i class="fa-solid fa-circle-play"></i> Start a project</button>
            <button class="ws-chip" data-info="systems" type="button"><i class="fa-solid fa-layer-group"></i> See what this branch offers</button>
            <button class="ws-chip" data-info="roles" type="button"><i class="fa-solid fa-users"></i> Who it is for</button>
          </div>
          <p class="dim ws-note">A project decides which of these systems it calls for from a few answers; nothing is picked from a menu.</p>
        </section>`}

        <div class="ws-section">
          <div class="ws-section-title">In the Info dock</div>
          <div class="ws-chips">
            <button class="ws-chip" data-info="systems" type="button"><i class="fa-solid fa-layer-group"></i> Systems and families</button>
            <button class="ws-chip" data-info="phases" type="button"><i class="fa-solid fa-stairs"></i> Phases</button>
            <button class="ws-chip" data-info="types" type="button"><i class="fa-solid fa-building"></i> ${d.id === 'building' ? 'Building types' : 'Site types'}</button>
            <button class="ws-chip" data-info="standards" type="button"><i class="fa-solid fa-list-check"></i> What holds</button>
          </div>
        </div>

        ${d.id === 'building' ? `
        <details class="ws-details ws-tool" data-joints>
          <summary>Read a .3dm of joints <span class="dim">· a proof tool for SD and DD</span></summary>
          <div class="ws-joints-bar">
            <label class="ws-link-btn ws-file-btn"><i class="fa-solid fa-file-import"></i> Read a .3dm of JT- blocks<input type="file" accept=".3dm" data-joints-upload hidden></label>
            <span class="ws-joints-hint">The lab reads the blocks back through the detail chain and returns the DT-S-502 sheet with what each block still needs. A readback, not an authored detail. <button class="ws-link-btn" data-info="hood" type="button" style="padding:1px 7px">How this reads</button></span>
          </div>
          <div class="ws-joints-out" data-joints-out hidden></div>
        </details>` : ''}
      </div>`;
    this.wireView(container);
  }

  private wireView(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('[data-info]').forEach(b => b.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('ws-info', { detail: { section: b.dataset.info } }));
    }));
    root.querySelectorAll<HTMLElement>('[data-nav]').forEach(b => b.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('navigate', { detail: { service: b.dataset.nav } }));
    }));
    const upload = root.querySelector<HTMLInputElement>('input[data-joints-upload]');
    const out = root.querySelector<HTMLElement>('[data-joints-out]');
    if (upload && out) upload.addEventListener('change', () => { const f = upload.files?.[0]; if (f) void this.readJoints(f, out); upload.value = ''; });
  }

  /** Send a Rhino .3dm to /api/ifc/joints and show the readback: hops, blocks and needs, the DT-S-502 sheet, a PDF link. */
  private async readJoints(file: File, out: HTMLElement): Promise<void> {
    out.hidden = false;
    out.innerHTML = `<div class="ws-empty">Reading ${esc(file.name)} (${(file.size / 1024).toFixed(0)} KB) through the detail chain…</div>`;
    try {
      const buf = await file.arrayBuffer();
      let bin = ''; const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      const r = await fetch('/api/ifc/joints', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fileName: file.name, content: btoa(bin) }) });
      const j = await r.json();
      if (!j.success) { out.innerHTML = `<div class="ws-empty">${esc(j.error || 'The lab could not read this file.')}</div>`; return; }
      const sheet = j.sheet || {};
      const pdf = sheet.pdfBase64 ? `<a class="ws-link-btn" download="${esc(sheet.pdfName || 'DT-S-502.pdf')}" href="data:application/pdf;base64,${sheet.pdfBase64}"><i class="fa-solid fa-file-pdf"></i> ${esc(sheet.number || 'DT-S-502')} PDF</a>` : '';
      const statusPill = (s: string) => `<span class="ws-pill" data-status="${s.startsWith('works') ? 'available' : (s === 'prototype' || s === 'stub') ? 'in-test' : 'planned'}">${esc(s)}</span>`;
      out.innerHTML = `
        <div class="ws-joints-head"><b>${esc(j.file)}</b> · ${j.blocks.length} block${j.blocks.length === 1 ? '' : 's'} · ${j.gaps} contract gap${j.gaps === 1 ? '' : 's'} ${pdf}</div>
        <table class="ws-table ws-joints-blocks">
          <thead><tr><th>Block</th><th>Parts</th><th>Roles</th><th>Placed at (mm)</th><th>Needs</th></tr></thead>
          <tbody>${j.blocks.map((b: any) => `<tr><td class="mono">${esc(b.name)}</td><td>${b.parts}</td><td>${b.roles.length ? b.roles.map(esc).join(', ') : '<span class="dim">none</span>'}</td><td class="mono">${b.translation.map((v: number) => Math.round(v)).join(', ')}</td><td>${b.needs.length ? `<ul class="ws-needs">${b.needs.map((n: string) => `<li>${esc(n)}</li>`).join('')}</ul>` : '<span class="ws-pill" data-level="required">meets the contract</span>'}</td></tr>`).join('')}</tbody>
        </table>
        ${j.placement?.length ? `<div class="ws-joints-placement">${j.placement.map((p: string) => esc(p)).join('<br>')}</div>` : ''}
        <div class="ws-joints-hops">${j.hops.map((h: any) => `<div title="${esc(h.detail)}">${statusPill(h.status)} ${esc(h.hop)}</div>`).join('')}</div>
        ${sheet.svg ? `<div class="ws-joints-sheet">${sheet.svg}</div>` : ''}`;
    } catch (e) {
      out.innerHTML = `<div class="ws-empty">Readback failed: ${esc((e as Error).message)}</div>`;
    }
  }

  private initializeWorkflowContext(): void {
    if (!this.workflowContext) {
      this.workflowContext = { selectedElements: [], analysisPhase: "conceptual", properties: new Map() };
    }
  }

  public getCurrentService(): AllServices { return this.currentService; }
  public getServiceState(serviceId: AllServices): ServiceState | undefined { return this.serviceStates.get(serviceId); }
  public getWorkflowContext(): DigitalTwinWorkflowContext | null { return this.workflowContext; }

  // ── Admin ─────────────────────────────────────────────────────────

  private async renderAdminService(container: HTMLElement): Promise<void> {
    container.innerHTML = '<div id="admin-panel-container" class="full-screen"></div>';
    const adminContainer = document.getElementById("admin-panel-container");
    if (!adminContainer) return;
    const { ProtectedAdminRoute } = await import("./ProtectedAdminRoute.js");
    const protectedRoute = new ProtectedAdminRoute(adminContainer);
    this.protectedAdminRoute = protectedRoute;
    protectedRoute.initialize();
    if (this.pendingAdminTab === 'viewer') {
      setTimeout(() => { protectedRoute.switchToViewer(); this.pendingAdminTab = null; }, 100);
    }
  }

  public updateWorkflowContext(updates: Partial<DigitalTwinWorkflowContext>): void {
    if (!this.workflowContext) this.initializeWorkflowContext();
    if (this.workflowContext) {
      Object.assign(this.workflowContext, updates);
      const state = this.serviceStates.get(this.currentService);
      if (state) state.context = this.workflowContext;
    }
  }

  public async switchToProtectedService(serviceId: 'admin', tab?: string): Promise<void> {
    this.pendingAdminTab = tab || null;
    if (this.currentService === 'admin' && this.protectedAdminRoute && tab === 'viewer') {
      this.protectedAdminRoute.switchToViewer();
      this.pendingAdminTab = null;
      return;
    }
    this.saveServiceState(this.currentService);
    this.currentService = serviceId;
    this.serviceStates.get(serviceId)!.isActive = true;
    await this.renderServiceArea();
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}
